import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Eye,
  Heart,
  MapPin,
  MessageCircle,
  MousePointer2,
  Phone,
  RefreshCw,
  Share2,
  Trophy,
  Upload,
  Users,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../../shared/apiService";
import { cn } from "../../utils/cn";
import { getPlatformConfig } from "../../utils/platformIcons";
import Card from "../ui/Card";
import Tooltip from "../ui/Tooltip";
import PostRankingSkeleton from "./skeletons/PostRankingSkeleton";

export type SortMetric =
  | "engagement_rate"
  | "impressions"
  | "likes"
  | "comments"
  | "shares";

export interface PostMetrics {
  impressions?: number;
  clicks?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  engagement_rate?: number;
  reach?: number;
}

export interface RankedPost {
  id: number;
  content: string;
  platform: string;
  published_at: string;
  metrics: PostMetrics;
  linkedin_url?: string;
  linkedin_post_id?: string;
  facebook_post_id?: string;
  instagram_post_id?: string;
}

export interface GmbMetrics {
  impressions: number;
  search_impressions: number;
  maps_impressions: number;
  direction_requests: number;
  website_clicks: number;
  phone_calls: number;
  total_clicks: number;
  locations_count: number;
}

export interface PlatformStats {
  platform: string;
  posts: number;
  impressions: number;
  likes: number;
  comments: number;
  shares: number;
  clicks: number;
  reach: number;
  engagements: number;
  avg_engagement_rate: number;
  engagement_rate_sum: number;
  posts_with_rate: number;
}

export const SORT_OPTIONS: {
  value: SortMetric;
  label: string;
  icon: React.ReactNode;
}[] = [
  {
    value: "engagement_rate",
    label: "Engajamento",
    icon: <Activity className="h-3.5 w-3.5" />,
  },
  {
    value: "impressions",
    label: "Impressões",
    icon: <Eye className="h-3.5 w-3.5" />,
  },
  {
    value: "likes",
    label: "Curtidas",
    icon: <Heart className="h-3.5 w-3.5" />,
  },
  {
    value: "comments",
    label: "Comentários",
    icon: <MessageCircle className="h-3.5 w-3.5" />,
  },
  {
    value: "shares",
    label: "Compartilhamentos",
    icon: <Share2 className="h-3.5 w-3.5" />,
  },
];

const MEDAL_STYLES = [
  {
    position: "1°",
    cardClass:
      "border-amber-300 bg-linear-to-br from-amber-50 to-orange-50/50 shadow-md shadow-amber-100",
    badgeClass: "from-yellow-400 to-amber-500 shadow-amber-300",
    valueClass: "text-amber-700",
  },
  {
    position: "2°",
    cardClass:
      "border-slate-300 bg-linear-to-br from-slate-50 to-gray-50/50 shadow-xs shadow-slate-100",
    badgeClass: "from-slate-300 to-slate-500 shadow-slate-300",
    valueClass: "text-slate-600",
  },
  {
    position: "3°",
    cardClass:
      "border-orange-300 bg-linear-to-br from-orange-50 to-amber-50/50 shadow-xs shadow-orange-100",
    badgeClass: "from-orange-300 to-amber-600 shadow-orange-300",
    valueClass: "text-orange-700",
  },
];

export function fmt(value: number | undefined, metric?: SortMetric): string {
  if (value === undefined || value === null) return "—";
  if (metric === "engagement_rate") return `${value.toFixed(2)}%`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString("pt-BR");
}

export function buildPlatformStats(posts: RankedPost[]): PlatformStats[] {
  const map: Record<string, PlatformStats> = {};

  for (const post of posts) {
    const p = (post.platform || "outro").toLowerCase();
    if (!map[p]) {
      map[p] = {
        platform: p,
        posts: 0,
        impressions: 0,
        likes: 0,
        comments: 0,
        shares: 0,
        clicks: 0,
        reach: 0,
        engagements: 0,
        avg_engagement_rate: 0,
        engagement_rate_sum: 0,
        posts_with_rate: 0,
      };
    }
    const s = map[p];
    const m = post.metrics;
    s.posts += 1;
    s.impressions += m.impressions ?? 0;
    s.likes += m.likes ?? 0;
    s.comments += m.comments ?? 0;
    s.shares += m.shares ?? 0;
    s.clicks += m.clicks ?? 0;
    s.reach += m.reach ?? 0;
    s.engagements += (m.likes ?? 0) + (m.comments ?? 0) + (m.shares ?? 0);
    if (m.engagement_rate != null && m.engagement_rate > 0) {
      s.engagement_rate_sum += m.engagement_rate;
      s.posts_with_rate += 1;
    }
  }

  return Object.values(map)
    .map((s) => ({
      ...s,
      avg_engagement_rate:
        s.posts_with_rate > 0
          ? parseFloat((s.engagement_rate_sum / s.posts_with_rate).toFixed(2))
          : s.reach > 0
            ? parseFloat(((s.engagements / s.reach) * 100).toFixed(2))
            : 0,
    }))
    .sort((a, b) => b.engagements - a.engagements);
}

// ─── Subcomponente: Google Meu Negócio ───────────────────────────────────────

export const GmbCard: React.FC<{ gmb: GmbMetrics }> = ({ gmb }) => (
  <Card
    variant="default"
    size="sm"
    className="border-purple-200! shadow-xs hover:shadow-md transition-all [&>div]:flex [&>div]:flex-col [&>div]:gap-3 w-full"
  >
    <div className="flex items-center justify-between mb-1 shrink-0">
      <div className="flex items-center gap-2.5">
        <div className="p-2 rounded-lg bg-green-50 border border-purple-200 flex items-center justify-center shrink-0">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        </div>
        <div>
          <p className="text-sm font-bold text-gray-800 leading-tight">
            Google Meu Negócio
          </p>
          <p className="text-[10px] text-gray-400 font-medium mt-0.5">
            {gmb.locations_count} local{gmb.locations_count !== 1 ? "is" : ""}{" "}
            monitorado{gmb.locations_count !== 1 ? "s" : ""}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-xl font-black text-green-600 leading-none">
          {fmt(gmb.total_clicks)}
        </p>
        <p className="text-[9px] text-gray-400 uppercase tracking-wider font-bold mt-1">
          Total ações
        </p>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-2 flex-1">
      <div className="bg-blue-50/30 border border-purple-200 rounded-xl p-3 flex flex-col justify-between min-h-[76px]">
        <div className="flex items-center gap-1.5 mb-1">
          <Eye className="h-3.5 w-3.5 text-blue-500" />
          <span className="text-[9px] font-black text-blue-600 uppercase tracking-wider">
            Impressões Maps
          </span>
        </div>
        <p className="text-base font-black text-blue-800">
          {fmt(gmb.maps_impressions ?? gmb.impressions)}
        </p>
      </div>
      <div className="bg-purple-50/30 border border-purple-200 rounded-xl p-3 flex flex-col justify-between min-h-[76px]">
        <div className="flex items-center gap-1.5 mb-1">
          <MapPin className="h-3.5 w-3.5 text-purple-500" />
          <span className="text-[9px] font-black text-purple-600 uppercase tracking-wider">
            Direções
          </span>
        </div>
        <p className="text-base font-black text-purple-800">
          {fmt(gmb.phone_calls ? gmb.direction_requests : 0)}
        </p>
      </div>
      <div className="bg-green-50/30 border border-purple-200 rounded-xl p-3 flex flex-col justify-between min-h-[76px]">
        <div className="flex items-center gap-1.5 mb-1">
          <MousePointer2 className="h-3.5 w-3.5 text-green-500" />
          <span className="text-[9px] font-black text-green-600 uppercase tracking-wider">
            Cliques Site
          </span>
        </div>
        <p className="text-base font-black text-green-800">
          {fmt(gmb.website_clicks)}
        </p>
      </div>
      <div className="bg-orange-50/30 border border-purple-200 rounded-xl p-3 flex flex-col justify-between min-h-[76px]">
        <div className="flex items-center gap-1.5 mb-1">
          <Phone className="h-3.5 w-3.5 text-orange-500" />
          <span className="text-[9px] font-black text-orange-600 uppercase tracking-wider">
            Ligações
          </span>
        </div>
        <p className="text-base font-black text-orange-800">
          {fmt(gmb.phone_calls)}
        </p>
      </div>
    </div>
  </Card>
);

export const GmbCardSkeleton: React.FC = () => (
  <Card
    variant="default"
    size="sm"
    className="border-purple-200! animate-pulse w-full [&>div]:flex [&>div]:flex-col [&>div]:gap-3"
  >
    <div className="flex items-center gap-2.5 mb-2 shrink-0">
      <div className="w-9 h-9 rounded-lg bg-gray-200" />
      <div className="space-y-1.5">
        <div className="h-3 w-28 bg-gray-200 rounded-sm" />
        <div className="h-2.5 w-20 bg-gray-100 rounded-sm" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2 flex-1">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="rounded-xl p-3 bg-gray-50 border border-purple-200 min-h-[76px] flex flex-col justify-between"
        >
          <div className="h-2.5 w-16 bg-gray-200 rounded-sm" />
          <div className="h-4.5 w-12 bg-gray-300 rounded-sm" />
        </div>
      ))}
    </div>
  </Card>
);

// ─── Subcomponente: Dashboard por Plataforma ────────────────────────────────

interface CsvImportResult {
  updated: number;
  not_found: number;
  skipped: number;
  total_rows: number;
  detected_columns: string[];
  raw_columns?: string[];
  first_row_sample?: Record<string, string>;
}

interface PlatformDashboardProps {
  stats: PlatformStats[];
  gmb?: GmbMetrics | null;
  gmbLoading?: boolean;
  onSyncDone?: () => void;
}

export const PlatformDashboard: React.FC<PlatformDashboardProps> = ({
  stats,
  gmb,
  gmbLoading,
  onSyncDone,
}) => {
  const [syncing, setSyncing] = useState<string | null>(null);
  const [diagMsg, setDiagMsg] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState<CsvImportResult | null>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  const handleSync = async (platform: string) => {
    setSyncing(platform);
    setDiagMsg(null);
    try {
      if (platform === "linkedin") {
        try {
          const cfgs = await apiService.get<any>("/api/social-config");
          const liCfg = cfgs?.configs?.linkedin ?? cfgs?.linkedin ?? null;
          if (liCfg && !liCfg.linkedin_organization_id) {
            setDiagMsg(
              "Organization ID do LinkedIn não configurado. Acesse Configurações → LinkedIn → Organization ID (Page) e preencha o ID numérico da sua Página de Empresa. Sem ele, impressões e alcance não ficam disponíveis.",
            );
            setSyncing(null);
            return;
          }
        } catch (_ignored) {}
      }

      const result = await apiService.post<any>(
        "/api/posts/sync-metrics-bulk",
        { platform, limit: 200 },
      );
      onSyncDone?.();
      setSyncSuccess(platform);
      setTimeout(() => setSyncSuccess(null), 3000);

      if (platform === "linkedin" && result) {
        const updated = result.updated ?? 0;
        if (updated > 0) {
          setDiagMsg(null);
        }
      }
    } catch (e: any) {
      console.error("Erro ao sincronizar métricas:", e);
      setDiagMsg(
        "Erro ao sincronizar métricas. Reinicie o backend se acabou de atualizar o código.",
      );
    } finally {
      setSyncing(null);
    }
  };

  const handleCsvImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvImporting(true);
    setCsvResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const result = await apiService.post<CsvImportResult>(
        "/api/integrations/linkedin/import-analytics",
        formData,
      );
      setCsvResult(result);
      if (result.updated > 0) onSyncDone?.();
    } catch (err: any) {
      setDiagMsg(
        "Erro ao importar CSV: " +
          (err?.message ?? "verifique o formato do arquivo"),
      );
    } finally {
      setCsvImporting(false);
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  };

  if (stats.length === 0 && !gmb && !gmbLoading) return null;

  const syncablePlatforms = ["linkedin", "facebook", "instagram"];

  return (
    <div className="space-y-2 w-full flex flex-col">
      {/* Input oculto para seleção do CSV */}
      <input
        ref={csvInputRef}
        type="file"
        accept=".xls,.xlsx,.csv"
        className="hidden"
        onChange={handleCsvImport}
      />

      {/* Modal de resultado da importação */}
      {csvResult && (
        <div
          className={`flex items-start gap-3 p-3.5 rounded-xl text-xs border ${
            csvResult.updated > 0
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-amber-50 border-amber-200 text-amber-800"
          }`}
        >
          {csvResult.updated > 0 ? (
            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-green-500" />
          ) : (
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
          )}
          <div className="flex-1 space-y-1.5">
            <p className="font-bold text-sm">
              {csvResult.updated > 0
                ? "Importado com sucesso"
                : "Arquivo lido, mas nenhum post cruzado"}
            </p>
            <p>
              <span className="font-black">{csvResult.updated}</span> posts
              atualizados ·{" "}
              <span className="font-black">{csvResult.not_found}</span> não
              encontrados · {csvResult.total_rows} linhas no arquivo
            </p>
          </div>
          <button
            onClick={() => setCsvResult(null)}
            className="text-amber-400 hover:text-amber-600 shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {diagMsg && (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
          <span className="flex-1">{diagMsg}</span>
          <Tooltip content="Dispensar alerta" position="top">
            <button
              onClick={() => setDiagMsg(null)}
              className="text-amber-400 hover:text-amber-600 shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      )}

      <div className="flex items-start gap-3 border-b pb-2 border-purple-100 mb-3 shrink-0">
        <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-purple-600 rounded-xl text-white shadow-xs">
          <Activity className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0 h-10 flex flex-col justify-between">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight leading-none">
            Performance por Canal
          </h3>
          <span className="text-xs text-gray-400 font-medium leading-none">
            Métricas consolidadas por rede social
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
        {gmbLoading ? <GmbCardSkeleton /> : gmb && <GmbCard gmb={gmb} />}
        {stats.map((s) => {
          const cfg = getPlatformConfig(s.platform);
          const PlatformIcon = cfg.icon;
          const isSyncing = syncing === s.platform;

          return (
            <Card
              key={s.platform}
              variant="default"
              size="sm"
              className="border-purple-200! shadow-xs hover:shadow-md transition-all [&>div]:flex [&>div]:flex-col [&>div]:gap-3 w-full"
            >
              {/* Header da plataforma */}
              <div className="flex items-center justify-between mb-1 shrink-0">
                <div className="flex items-center gap-2.5">
                  <div
                    className={cn(
                      "p-2 rounded-lg border flex items-center justify-center shrink-0",
                      cfg.bgColor,
                      cfg.borderColor,
                    )}
                  >
                    <PlatformIcon className={cn("h-5 w-5", cfg.color)} />
                  </div>
                  <div>
                    <p className="flex items-center gap-1.5 text-sm font-bold text-gray-800 capitalize leading-tight">
                      {cfg.name}
                      {s.platform === "linkedin" && s.impressions === 0 && (
                        <Tooltip
                          content="Impressões e alcance requerem LinkedIn Marketing Partner Program."
                          position="top"
                          wrapperClassName="flex shrink-0"
                          usePortal
                        >
                          <AlertCircle className="h-3.5 w-3.5 text-amber-500 cursor-help outline-hidden" />
                        </Tooltip>
                      )}
                      {s.platform === "facebook" &&
                        s.impressions === 0 &&
                        s.clicks === 0 &&
                        s.reach === 0 && (
                          <Tooltip
                            content="Impressões e cliques requerem a permissão pages_read_engagement."
                            position="top"
                            wrapperClassName="flex shrink-0"
                            usePortal
                          >
                            <AlertCircle className="h-3.5 w-3.5 text-amber-500 cursor-help outline-hidden" />
                          </Tooltip>
                        )}
                    </p>
                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                      {s.posts} post{s.posts !== 1 ? "s" : ""} rastreado
                      {s.posts !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <div className="text-right">
                    {(s.platform === "linkedin" && s.impressions === 0) ||
                    (s.platform === "facebook" &&
                      s.impressions === 0 &&
                      s.clicks === 0 &&
                      s.engagements === 0 &&
                      s.reach === 0) ? (
                      <p className="text-xl font-black text-slate-400 leading-none">
                        N/D
                      </p>
                    ) : (
                      <p className="text-xl font-black text-indigo-600 leading-none">
                        {s.avg_engagement_rate.toFixed(2)}%
                      </p>
                    )}
                    <p className="text-[9px] text-gray-400 uppercase tracking-wider font-bold mt-1">
                      Eng. médio
                    </p>
                  </div>
                </div>
              </div>

              {/* Grade de métricas */}

              <div className="grid grid-cols-2 gap-2 flex-1">
                <div className="bg-blue-50/30 border border-purple-200 rounded-xl p-3 flex flex-col justify-between min-h-[76px]">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Eye className="h-3.5 w-3.5 text-blue-500" />
                    <span className="text-[9px] font-black text-blue-600 uppercase tracking-wider">
                      Impressões
                    </span>
                  </div>
                  <p className="text-base font-black text-blue-800">
                    {s.platform === "linkedin" && s.impressions === 0 ? (
                      <span className="text-slate-400">N/D</span>
                    ) : (
                      fmt(s.impressions)
                    )}
                  </p>
                </div>

                <div className="bg-purple-50/30 border border-purple-200 rounded-xl p-3 flex flex-col justify-between min-h-[76px]">
                  <div className="flex items-center gap-1.5 mb-1">
                    {s.platform === "linkedin" ? (
                      <Heart className="h-3.5 w-3.5 text-purple-500" />
                    ) : (
                      <Users className="h-3.5 w-3.5 text-purple-500" />
                    )}
                    <span className="text-[9px] font-black text-purple-600 uppercase tracking-wider">
                      {s.platform === "linkedin" ? "Reações" : "Alcance"}
                    </span>
                  </div>
                  <p className="text-base font-black text-purple-800">
                    {s.platform === "linkedin" ? (
                      fmt(s.likes)
                    ) : s.reach === 0 ? (
                      <span className="text-slate-400">N/D</span>
                    ) : (
                      fmt(s.reach)
                    )}
                  </p>
                </div>

                <div className="bg-rose-50/30 border border-purple-200 rounded-xl p-3 flex flex-col justify-between min-h-[76px]">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Heart className="h-3.5 w-3.5 text-rose-500" />
                    <span className="text-[9px] font-black text-rose-600 uppercase tracking-wider">
                      Engajamentos
                    </span>
                  </div>
                  <p className="text-base font-black text-rose-800">
                    {fmt(s.engagements)}
                  </p>
                </div>

                <div className="bg-orange-50/30 border border-purple-200 rounded-xl p-3 flex flex-col justify-between min-h-[76px]">
                  <div className="flex items-center gap-1.5 mb-1">
                    <MousePointer2 className="h-3.5 w-3.5 text-orange-500" />
                    <span className="text-[9px] font-black text-orange-600 uppercase tracking-wider">
                      Cliques
                    </span>
                  </div>
                  <p className="text-base font-black text-orange-800">
                    {s.platform === "linkedin" && s.clicks === 0 ? (
                      <span className="text-slate-400">N/D</span>
                    ) : (
                      fmt(s.clicks)
                    )}
                  </p>
                </div>
              </div>

              {/* Rodapé de ações de Sincronização */}
              <div className="flex items-center justify-between border-t border-purple-100 pt-2 shrink-0">
                {s.avg_engagement_rate > 0 ? (
                  <div className="flex items-center gap-2 flex-1 mr-3">
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-linear-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-1000"
                        style={{
                          width: `${Math.min(s.avg_engagement_rate * 10, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex-1" />
                )}

                {syncablePlatforms.includes(s.platform) && (
                  <div className="flex items-center gap-2.5 shrink-0">
                    {s.platform === "linkedin" && (
                      <Tooltip
                        content="Importar CSV"
                        position="top"
                        wrapperClassName="flex"
                      >
                        <button
                          onClick={() => csvInputRef.current?.click()}
                          disabled={csvImporting || !!syncing}
                          className="flex items-center gap-1 text-[9px] font-bold text-indigo-500 hover:text-indigo-700 disabled:opacity-40 transition-colors"
                        >
                          <Upload
                            className={cn(
                              "h-3 w-3",
                              csvImporting && "animate-pulse",
                            )}
                          />
                          {csvImporting ? "Importando…" : "Importar CSV"}
                        </button>
                      </Tooltip>
                    )}
                    {syncSuccess === s.platform ? (
                      <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-green-50 text-[9px] font-bold text-green-600 border border-green-200 transition-all">
                        <CheckCircle2 className="h-3 w-3" />
                        Sincronizado
                      </div>
                    ) : (
                      <Tooltip
                        content="Sincronizar"
                        position="top"
                        wrapperClassName="flex"
                      >
                        <button
                          onClick={() => handleSync(s.platform)}
                          disabled={!!syncing || csvImporting}
                          className="flex items-center gap-1 text-[9px] font-bold text-purple-600 hover:text-purple-800 disabled:opacity-40 transition-colors"
                        >
                          <RefreshCw
                            className={cn(
                              "h-3 w-3",
                              isSyncing && "animate-spin",
                            )}
                          />
                          {isSyncing ? "Sincronizando…" : "Sincronizar"}
                        </button>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// ─── Dropdown customizado de ordenação ───────────────────────────────────────

const SortDropdownInner: React.FC<{
  sortBy: SortMetric;
  setSortBy: (m: SortMetric) => void;
}> = ({ sortBy, setSortBy }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = SORT_OPTIONS.find((o) => o.value === sortBy)!;

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "h-8 flex items-center gap-1.5 pl-2.5 pr-2 rounded-lg text-xs font-bold border shadow-xs transition-all",
          open
            ? "border-purple-400 bg-purple-50 text-purple-700 ring-2 ring-purple-200"
            : "border-purple-200 bg-white text-gray-700 hover:border-purple-400 hover:text-purple-700",
        )}
      >
        <span className="text-purple-500 flex items-center">{active.icon}</span>
        <span className="ml-0.5">{active.label}</span>
        <svg
          className={cn(
            "h-3 w-3 text-gray-400 transition-transform duration-150",
            open && "rotate-180",
          )}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Opções */}
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-44 bg-white border border-purple-200 rounded-xl shadow-lg py-1 animate-in fade-in slide-in-from-top-1 duration-100">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setSortBy(opt.value);
                setOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold transition-colors",
                sortBy === opt.value
                  ? "bg-purple-50 text-purple-700"
                  : "text-gray-600 hover:bg-gray-50 hover:text-purple-700",
              )}
            >
              <span
                className={cn(
                  "flex items-center",
                  sortBy === opt.value ? "text-purple-500" : "text-gray-400",
                )}
              >
                {opt.icon}
              </span>
              {opt.label}
              {sortBy === opt.value && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-purple-500" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// React.memo: evita re-render do dropdown quando o pai re-renderiza por outras razões
const SortDropdown = React.memo(SortDropdownInner);

// ─── Componente Principal PostRanking (Apresentador de Dados) ─────────────────

interface PostRankingProps {
  posts: RankedPost[];
  loading: boolean;
  error: string | null;
  sortBy: SortMetric;
  setSortBy: (metric: SortMetric) => void;
  fetchPosts: () => void;
}

const PostRanking: React.FC<PostRankingProps> = ({
  posts,
  loading,
  error,
  sortBy,
  setSortBy,
  fetchPosts,
}) => {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.resolve(fetchPosts());
    } finally {
      // Garantia: remove o estado de refresh mesmo se fetchPosts não retornar Promise
      setTimeout(() => setRefreshing(false), 400);
    }
  };

  const activeSortOption = SORT_OPTIONS.find((o) => o.value === sortBy)!;

  if (loading) return <PostRankingSkeleton />;

  return (
    <div className="space-y-2 animate-in fade-in duration-300 w-full flex flex-col">
      {/* ── Cabeçalho do ranking ── */}
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-purple-100 pb-2 mb-3 shrink-0">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 bg-amber-500 rounded-xl text-white shadow-xs shrink-0 flex items-center justify-center">
            <Trophy className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0 h-10 flex flex-col justify-between">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight leading-none">
              Ranking de Posts
            </h3>
            <span className="text-xs text-gray-400 font-medium leading-none">
              {posts.length} post{posts.length !== 1 ? "s" : ""} com métricas ·
              ordenado por{" "}
              <span className="text-purple-600 font-bold">
                {activeSortOption.label.toLowerCase()}
              </span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Dropdown de ordenação customizado com ícones */}
          <SortDropdown sortBy={sortBy} setSortBy={setSortBy} />

          {/* Botão Atualizar — mesma altura h-8 do SortDropdown trigger */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className={cn(
              "h-8 flex items-center gap-1.5 pl-2.5 pr-3 rounded-lg text-xs font-bold border shadow-xs transition-all",
              refreshing
                ? "border-purple-300 bg-purple-50 text-purple-400 cursor-not-allowed"
                : "border-purple-200 bg-white text-purple-700 hover:border-purple-400 hover:bg-purple-50",
            )}
          >
            <RefreshCw
              className={cn("h-3.5 w-3.5", refreshing && "animate-spin")}
            />
            <span>{refreshing ? "Atualizando…" : "Atualizar"}</span>
          </button>
        </div>
      </div>

      {/* ── Estado vazio ── */}
      {posts.length === 0 && !error && (
        <Card className="p-6 text-center border-dashed border-2 border-purple-200 bg-purple-50/30 rounded-xl">
          <div className="flex flex-col items-center gap-3">
            <div className="p-4 rounded-xl bg-purple-100">
              <Trophy className="h-8 w-8 text-purple-400" />
            </div>
            <p className="font-bold text-gray-700">
              Nenhum post encontrado neste período
            </p>
            <p className="text-xs text-gray-500 max-w-xs leading-relaxed">
              Tente ampliar o período ou sincronize métricas na aba de posts
              publicados.
            </p>
          </div>
        </Card>
      )}

      {error && (
        <Card className="p-4 border-red-200 bg-red-50 rounded-xl flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
          <p className="text-xs text-red-700 font-medium">{error}</p>
        </Card>
      )}

      {/* ── Top 3 — pódio ── */}
      {posts.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 shrink-0">
            {posts.slice(0, 3).map((post, idx) => {
              const style = MEDAL_STYLES[idx];
              const cfg = getPlatformConfig(post.platform);
              const PlatformIcon = cfg.icon;
              const metricVal = (post.metrics as any)?.[sortBy];

              return (
                <Card
                  key={post.id}
                  onClick={() =>
                    navigate(`/posts?filter=published&highlight=${post.id}`)
                  }
                  className={cn(
                    "relative border-2 transition-all hover:shadow-md cursor-pointer group flex flex-col min-h-[160px] [&>div]:flex-1 [&>div]:flex [&>div]:flex-col [&>div]:justify-between [&>div]:gap-2 [&>div]:min-h-0",
                    style.cardClass,
                  )}
                >
                  {/* Cabeçalho do Card Integrado */}
                  <div className="flex items-center justify-between mb-3 shrink-0">
                    <div className="flex items-center gap-2.5">
                      {/* Badge de posição integrado */}
                      <div
                        className={cn(
                          "w-7 h-7 rounded-lg bg-linear-to-br flex items-center justify-center shadow-xs text-xs font-black text-white shrink-0",
                          style.badgeClass,
                        )}
                      >
                        {style.position}
                      </div>

                      <div className="flex items-center gap-1.5">
                        <PlatformIcon
                          className={cn("h-4 w-4 shrink-0", cfg.color)}
                        />
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                          {cfg.name}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Indicador de link interno dinâmico com affordance mobile */}
                      <span className="text-[10px] font-bold text-purple-500 sm:text-gray-300 group-hover:text-purple-600 group-hover:bg-purple-50 sm:group-hover:px-2 sm:group-hover:py-1 sm:group-hover:-my-1 sm:group-hover:-mx-2 rounded-lg transition-all flex items-center gap-1 bg-purple-50 sm:bg-transparent px-2 py-1 -my-1 -mx-2 sm:p-0 sm:m-0">
                        <span className="inline sm:hidden group-hover:inline uppercase tracking-wider text-[9px]">
                          Ver
                        </span>
                        #{post.id}
                      </span>
                      {/* Métrica de destaque */}
                      <span
                        className={cn(
                          "text-xl font-black leading-none tracking-tight",
                          style.valueClass,
                        )}
                      >
                        {fmt(metricVal, sortBy)}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-700 font-medium line-clamp-2 leading-relaxed mb-2.5 flex-1">
                    {post.content}
                  </p>

                  {/* Mini métricas */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-purple-200/50 shrink-0">
                    {post.metrics.likes !== undefined && (
                      <span className="flex items-center gap-1 text-[9px] text-gray-500 font-bold">
                        <Heart className="h-3 w-3 text-red-400" />{" "}
                        {fmt(post.metrics.likes)}
                      </span>
                    )}
                    {post.metrics.comments !== undefined && (
                      <span className="flex items-center gap-1 text-[9px] text-gray-500 font-bold">
                        <MessageCircle className="h-3 w-3 text-green-400" />{" "}
                        {fmt(post.metrics.comments)}
                      </span>
                    )}
                    {post.metrics.shares !== undefined && (
                      <span className="flex items-center gap-1 text-[9px] text-gray-500 font-bold">
                        <Share2 className="h-3 w-3 text-purple-400" />{" "}
                        {fmt(post.metrics.shares)}
                      </span>
                    )}
                    {post.metrics.impressions !== undefined && (
                      <span className="flex items-center gap-1 text-[9px] text-gray-500 font-bold">
                        <Eye className="h-3 w-3 text-blue-400" />{" "}
                        {fmt(post.metrics.impressions)}
                      </span>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* ── Tabela: posições 4+ ── */}
          {posts.length > 3 && (
            <Card
              variant="default"
              size="sm"
              className="border-purple-200! overflow-hidden p-0! shadow-xs flex flex-col"
            >
              <div className="px-4 py-2 border-b border-purple-200 bg-purple-50/20 flex items-center justify-between shrink-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-purple-700">
                  Outros Posts
                </p>
                <p className="text-[10px] text-gray-400 font-medium">
                  {posts.length - 3} post{posts.length - 3 !== 1 ? "s" : ""}
                </p>
              </div>
              <div className="divide-y divide-purple-100">
                {posts.slice(3, 10).map((post, idx) => {
                  const cfg = getPlatformConfig(post.platform);
                  const PlatformIcon = cfg.icon;
                  const metricVal = (post.metrics as any)?.[sortBy];
                  const position = idx + 4;

                  return (
                    <div
                      key={post.id}
                      onClick={() =>
                        navigate(`/posts?filter=published&highlight=${post.id}`)
                      }
                      className={cn(
                        "flex items-center gap-3 px-4 py-2.5 hover:bg-purple-50/20 border-b border-purple-100 last:border-b-0 transition-colors group cursor-pointer",
                      )}
                    >
                      {/* Número de posição */}
                      <Tooltip
                        content={`Posição ${position}`}
                        position="top"
                        wrapperClassName="flex"
                      >
                        <span className="text-xs font-bold text-gray-300 w-6 text-center shrink-0 tabular-nums">
                          {position}
                        </span>
                      </Tooltip>

                      <span className="text-[9px] font-bold text-purple-400 w-8 text-left shrink-0">
                        #{post.id}
                      </span>

                      <PlatformIcon
                        className={cn("h-3.5 w-3.5 shrink-0", cfg.color)}
                      />

                      <p className="flex-1 text-xs text-gray-700 font-medium truncate min-w-0">
                        {post.content}
                      </p>

                      {/* Métricas inline */}
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="flex items-center gap-1 text-xs font-black text-gray-800 min-w-[50px] justify-end tabular-nums">
                          {activeSortOption.icon}
                          {fmt(metricVal, sortBy)}
                        </span>
                        {post.metrics.likes !== undefined && (
                          <span className="hidden sm:flex items-center gap-1 text-[9px] text-gray-400 font-bold tabular-nums">
                            <Heart className="h-3 w-3 text-red-300" />{" "}
                            {fmt(post.metrics.likes)}
                          </span>
                        )}
                        {post.metrics.comments !== undefined && (
                          <span className="hidden sm:flex items-center gap-1 text-[9px] text-gray-400 font-bold tabular-nums">
                            <MessageCircle className="h-3 w-3 text-green-300" />{" "}
                            {fmt(post.metrics.comments)}
                          </span>
                        )}
                        {post.metrics.impressions !== undefined && (
                          <span className="hidden md:flex items-center gap-1 text-[9px] text-gray-400 font-bold tabular-nums">
                            <Eye className="h-3 w-3 text-blue-300" />{" "}
                            {fmt(post.metrics.impressions)}
                          </span>
                        )}
                        <Tooltip
                          content="Ver post"
                          position="top"
                          wrapperClassName="flex"
                        >
                          <div className="p-1 rounded-lg text-purple-600 bg-purple-50 sm:bg-transparent sm:text-gray-300 group-hover:text-purple-600 group-hover:bg-purple-50 transition-all ml-1">
                            <span className="text-[9px] uppercase font-bold tracking-wider">
                              Ver
                            </span>
                          </div>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default PostRanking;
